import { Box, Button, Stack, Typography } from "@mui/material";
import React, { forwardRef, useContext, useEffect } from "react";
import Draggable from "react-draggable";
import { Link } from "react-router-dom";
import { useNavTileScale } from "./useNavTileScale.tsx";
import { NavItemContext, useNavItemProvider } from "./NavItemProvider.tsx";

type NavListItemProps = {
  title: string;
  path: string;
  top: number;
  left: number;
};

export const NavPopup = (props: NavListItemProps) => {
  const { top: initialTop, left: initialLeft, title, path } = props;
  const scale = useNavTileScale();
  const { focusedNavItem, setFocusedNavItem } = useContext(NavItemContext);

  const focused = focusedNavItem === path;

  const [top, setTop] = React.useState(initialTop + 800);
  const [left, setLeft] = React.useState(initialLeft + 800);
  //move the element from bottom right to the initial position (top, left) with a speed of 32px per frame
  useEffect(() => {
    if (top > initialTop) {
      setTop((prev) => prev - 16);
    }
    if (left > initialLeft) {
      setLeft((prev) => prev - 16);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [top, left]);

  const ref = React.useRef<HTMLDivElement>(null);
  const [tailCoordinates, setTailCoordinates] = React.useState<
    { top: number; left: number }[]
  >([]);

  //keep track of the past 10 coordinates of the element, to create a tail effect
  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === "attributes") {
        const elementCoordinates = ref.current?.getBoundingClientRect();
        if (elementCoordinates) {
          setTailCoordinates((prev) => {
            // add a new coordinate
            const newCoordinate = {
              top: elementCoordinates.top,
              left: elementCoordinates.left,
            };

            //push the new coordinate to the array, remove the first element if the array is too long (greater than 5)
            return [...prev, newCoordinate].slice(-10);
          });
        }
      }
    });
  });

  useEffect(() => {
    if (!ref.current) return;
    mutationObserver.observe(ref.current, {
      attributes: true,
    });

    return () => {
      mutationObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  //clear tail coordinates if they havne't changed in 0.5 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      setTailCoordinates([]);
    }, 500);

    return () => {
      clearInterval(intervalId);
    };
  }, [tailCoordinates]);

  return (
    <>
      <Draggable
        scale={scale}
        cancel=".view-button"
        onStart={() => setFocusedNavItem(path)}
      >
        <Box
          sx={{
            pointerEvents: "auto",
            position: "fixed",
            top,
            left,
            scale: scale,
            transformOrigin: "top left",
            zIndex: focused ? 501 : 500,
          }}
          ref={ref}
        >
          <NavListItemBase title={title} path={path} />
        </Box>
      </Draggable>

      {tailCoordinates.map((coordinate, index) => (
        <Box
          key={
            coordinate.top.toString() +
            coordinate.left.toString() +
            index.toString()
          }
          sx={{
            pointerEvents: "none",
            position: "fixed",
            top: coordinate.top,
            left: coordinate.left,
            scale,
            transformOrigin: "top left",
            animation: "fade 1s forwards",
            "@keyframes fade": {
              "0%": { opacity: 0.8 },
              "80:": { opacity: 0.8 },
              "100%": { opacity: 0 },
            },
            zIndex: 400,
          }}
        >
          <NavListItemBase title={title} path={path} />
        </Box>
      ))}
    </>
  );
};

const NavListItemBase = forwardRef(
  (props: Pick<NavListItemProps, "title" | "path">, ref) => {
    const { title, path } = props;
    const { clickedNavItem } = useNavItemProvider();

    const isClicked = clickedNavItem === path;

    return (
      <Box
        ref={ref}
        className="nav-popup"
        sx={(theme) => ({
          cursor: "grab",
          backgroundColor: theme.palette.background.default,
          width: 300,
          height: 200,
          boxShadow: `0px 5px 5px -3px ${theme.palette.text.primary}33,0px 8px 10px 1px  ${theme.palette.text.primary}22,0px 3px 14px 2px  ${theme.palette.text.primary}11`,
          display: "flex",
          textDecoration: "none",
          color: "text.primary",
          borderRadius: "8px",
          overflow: "hidden",
          border: `1px solid ${theme.palette.text.primary}`,
          ...(isClicked && {
            animation: "click 0.5s",
            //move back and forth 3 times
            "@keyframes click": {
              "0%": { transform: "translateX(0)" },
              "25%": { transform: "translateX(-4px)" },
              "50%": { transform: "translateX(4px)" },
              "75%": { transform: "translateX(-4px)" },
              "100%": { transform: "translateX(0)" },
            },
          }),
        })}
      >
        <Box
          sx={(theme) => ({
            padding: "24px 1px 1px",
            backgroundColor: theme.palette.primary.main,
            flex: 1,
            display: "flex",
          })}
        >
          <Stack
            sx={(theme) => ({
              padding: 2,
              textAlign: "center",
              backgroundColor: theme.palette.background.default,
              flex: 1,
              borderRadius: "6px",
              justifyContent: "space-around",
              alignItems: "center",
            })}
          >
            <Typography
              sx={(theme) => ({
                ...theme.typography.h5,
              })}
            >
              {title}
            </Typography>
            <Button
              className="view-button"
              variant="outlined"
              sx={(theme) => ({
                width: "50%",
              })}
              component={Link}
              to={path}
            >
              View
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  },
);
